import { useTranslation } from 'react-i18next';
import { Button, Text } from '@/shared/ui';
import { Meter } from '@/shared/ui/components/basic/Meter';
import type { TrialKind } from '@/shared/utils/trial-quota';

/** Everything the notice writes, already resolved from the data. */
export interface TrialNoticeModel {
  kind: TrialKind;
  level: 'warn' | 'block';
  used: number;
  total: number;
  /** Tier name of the plan being previewed ("Rubi"). */
  planName: string;
  /** That plan's monthly allowance of this quota, when the catalogue says. */
  planQuota: number | null;
  /** "R$ 454,90": what activating charges today, when known. */
  priceLabel: string | null;
  /** When the free period ends, written for the reader. Never a countdown. */
  waitDate: string | null;
}

interface Props {
  model: TrialNoticeModel;
  onActivate: () => void;
}

interface PartProps {
  model: TrialNoticeModel;
  onActivate?: () => void;
}

/**
 * The call to action and the price beside it, shared by both levels.
 *
 * @param props - The model and the action.
 * @returns The button and its price line.
 */
function ActivateAction({ model, onActivate }: PartProps) {
  const { t } = useTranslation('features/pricing');
  const priceKey = `subscription.trial.notice.${model.level}.price`;
  return (
    <div className="flex flex-col items-start gap-(--space-1)">
      <Button variant="solid" size="md" onClick={onActivate} data-testid="trial-notice-cta">
        {t('subscription.trial.notice.cta', { plan: model.planName })}
      </Button>
      {model.priceLabel && (
        <Text component="span" data-testid="trial-notice-price" style={{
          fontSize: 'var(--text-meta)', color: 'var(--text-secondary)',
        }}>
          {t(priceKey, { price: model.priceLabel })}
        </Text>
      )}
    </div>
  );
}

/**
 * The 80% warning: discreet, with the bar and the way out (RN-07).
 *
 * @param props - The model and the action.
 * @returns The warning.
 */
function WarnNotice({ model, onActivate }: Props) {
  const { t } = useTranslation('features/pricing');
  const bodyKey = model.planQuota ? 'body' : 'bodyNoQuota';
  return (
    <div
      role="status"
      data-testid="trial-quota-warn"
      className="flex flex-wrap items-center justify-between gap-(--space-3)"
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--status-warning-border, var(--border-subtle))',
        background: 'var(--status-warning-bg, var(--surface-secondary))',
      }}
    >
      <div className="flex flex-col gap-(--space-1)" style={{ flex: '1 1 320px' }}>
        <Text component="span" style={{ fontSize: 'var(--text-body-sm)' }}>
          <strong>{t('subscription.trial.notice.warn.title', { used: model.used, total: model.total })}</strong>
          {' '}
          {t(`subscription.trial.notice.warn.${bodyKey}`, { plan: model.planName, quota: model.planQuota })}
        </Text>
        <Meter
          ratio={model.used / model.total}
          tone="var(--accent-cobre)"
          label={t('subscription.plan.usage.interaction')}
          used={model.used}
          total={model.total}
          testId="trial-notice-bar"
        />
      </div>
      <ActivateAction model={model} onActivate={onActivate} />
    </div>
  );
}

/**
 * The 100% block: the feature has stopped, here is how to get it back (RN-07).
 *
 * @param props - The model and the action.
 * @returns The block.
 */
function BlockNotice({ model, onActivate }: Props) {
  const { t } = useTranslation('features/pricing');
  const { kind, planName, planQuota, waitDate, total } = model;
  const bodyKey = planQuota ? `body_${kind}` : 'bodyNoQuota';
  return (
    <div
      role="alert"
      data-testid="trial-quota-block"
      className="flex flex-col items-center gap-(--space-3) text-center"
      style={{
        padding: 'var(--space-6) var(--space-5)',
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border-default, var(--border-subtle))',
        background: 'var(--surface-secondary)',
      }}
    >
      <Text component="strong" style={{ fontSize: 'var(--text-lg)' }}>
        {t(`subscription.trial.notice.block.title_${kind}`, { total })}
      </Text>
      <Text component="span" style={{ fontSize: 'var(--text-body-sm)', maxWidth: 520 }}>
        {t(`subscription.trial.notice.block.${bodyKey}`, { plan: planName, quota: planQuota })}
      </Text>
      <ActivateAction model={model} onActivate={onActivate} />
      <WaitLine kind={kind} date={waitDate} />
    </div>
  );
}

/**
 * "Ou espere: a IA volta em 03/11/2026": the honest alternative, a date.
 *
 * @param props - The quota and the date, when known.
 * @returns The line, or nothing without a date.
 */
function WaitLine({ kind, date }: { kind: TrialKind; date: string | null }) {
  const { t } = useTranslation('features/pricing');
  if (!date) return null;
  return (
    <Text component="span" data-testid="trial-notice-wait" style={{
      fontSize: 'var(--text-meta)', color: 'var(--text-secondary)',
    }}>
      {t(`subscription.trial.notice.block.wait_${kind}`, { date })}
    </Text>
  );
}

/**
 * The trial-ceiling notice, presentational: a warning at 80% of the
 * interactions, a block at 100% of either quota (RN-07). The button hands
 * the decision to the caller; the copy never counts days (RN-17).
 *
 * @param props - The resolved model and the activation handler.
 * @returns The warning or the block.
 */
export function TrialQuotaNoticeView({ model, onActivate }: Props) {
  return model.level === 'warn'
    ? <WarnNotice model={model} onActivate={onActivate} />
    : <BlockNotice model={model} onActivate={onActivate} />;
}
